import { useState } from 'react';

import { isValidElement } from '@/shared/utils';

interface Position {
  top: number;
  left: number;
  height?: number;
}

const DEFAULT_POSITION: Position = {
  left: 0,
  top: 0,
  height: 0,
};

const DEFAULT_LINE_HEIGHT = 16;
const LINE_GAP = 4;
const POPOVER_GAP = 8;

const usePopoverPosition = () => {
  const [popoverPosition, setPopoverPosition] =
    useState<Position>(DEFAULT_POSITION);

  const getPopoverPosition = () => {
    if (typeof window === 'undefined') return;

    const getParentElement = (range: Range) => {
      let node: Node = range.commonAncestorContainer;
      if (!node) return;

      while (!isValidElement(node as Element)) {
        node = node.parentNode as Node;
      }
      return node as Element;
    };

    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return null;

    const range = selection.getRangeAt(0);
    const parentElement = getParentElement(range);

    if (!parentElement) return null;

    const parentRect = parentElement.getBoundingClientRect();
    const rangeRect = range.getBoundingClientRect();

    const getGapByTagName = () => {
      const parentTagname = parentElement.tagName;
      if (
        parentTagname === 'P' ||
        parentTagname === 'LI' ||
        parentTagname === 'SPAN'
      )
        return LINE_GAP - POPOVER_GAP;
      return POPOVER_GAP - LINE_GAP;
    };

    return {
      left: rangeRect.left - parentRect.left + rangeRect.width / 2,
      top: rangeRect.top - parentRect.top + getGapByTagName(),
      height: rangeRect.height + DEFAULT_LINE_HEIGHT,
    };
  };

  const updatePopoverPosition = () => {
    const position = getPopoverPosition();
    if (position) {
      setPopoverPosition(position);
    } else {
      setPopoverPosition(DEFAULT_POSITION);
    }
  };

  return { popoverPosition, updatePopoverPosition };
};

export default usePopoverPosition;
